// src/contexts/SubscriptionContext.tsx
// Fixed version that always checks user_profiles.is_pro_member as source of truth

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

// =====================================================
// TYPES
// =====================================================

interface SubscriptionPlan {
  id: string;
  name: string;
  duration_months: number;
  price: number;
  is_active: boolean;
  dodo_product_id?: string;
  daily_ai_scans_limit?: number;
  protocol_cooldown_days?: number;
}

interface SubscriptionStatus {
  user_id: string;
  is_pro: boolean;
  is_trial: boolean;
  is_expired: boolean;
  trial_days_left: number;
  subscription_days_left: number;
  plan_name: string;
  daily_ai_limit: number;
  ai_scans_used: number;
  ai_scans_remaining: number;
  can_use_ai_scanner: boolean;
  can_generate_protocol: boolean;
  days_until_protocol: number;
  protocol_generation_count: number;
}

interface DailyUsage {
  ai_scans_count: number;
  manual_entries_count: number;
}

interface SubscriptionContextType {
  // State
  subscriptionStatus: SubscriptionStatus | null;
  dailyUsage: DailyUsage;
  plans: SubscriptionPlan[];
  isLoading: boolean;
  
  // Computed
  isPro: boolean;
  isTrialActive: boolean;
  isExpired: boolean;
  canUseAIScanner: boolean;
  canGenerateProtocol: boolean;
  aiScansRemaining: number;
  daysUntilExpiry: number;
  
  // Actions
  refreshSubscriptionStatus: () => Promise<void>;
  checkAndIncrementAIScan: () => Promise<{ allowed: boolean; message: string; remaining?: number }>;
  checkProtocolGeneration: () => Promise<{ allowed: boolean; message: string; daysUntilNext?: number }>;
  recordProtocolGeneration: () => Promise<void>;
  createCheckoutSession: (planId: string) => Promise<{ checkout_url: string } | null>;
  loadPlans: () => Promise<void>;
}

// =====================================================
// CONSTANTS
// =====================================================

const FREE_DAILY_AI_LIMIT = 4;
const PRO_DAILY_AI_LIMIT = 8;

// =====================================================
// CONTEXT
// =====================================================

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage>({ ai_scans_count: 0, manual_entries_count: 0 });
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // =====================================================
  // COMPUTED VALUES - Derived from subscriptionStatus
  // =====================================================

  const isPro = subscriptionStatus?.is_pro ?? false;
  const isTrialActive = subscriptionStatus?.is_trial ?? false;
  const isExpired = subscriptionStatus?.is_expired ?? false;
  
  const dailyLimit = isPro ? PRO_DAILY_AI_LIMIT : FREE_DAILY_AI_LIMIT;
  const aiScansRemaining = Math.max(0, dailyLimit - dailyUsage.ai_scans_count);
  const canUseAIScanner = aiScansRemaining > 0 && !isExpired;
  const canGenerateProtocol = subscriptionStatus?.can_generate_protocol ?? true;
  
  const daysUntilExpiry = isPro 
    ? (subscriptionStatus?.subscription_days_left ?? 0) 
    : (subscriptionStatus?.trial_days_left ?? 0);

  // =====================================================
  // LOAD SUBSCRIPTION STATUS - Always check user_profiles
  // =====================================================

  const refreshSubscriptionStatus = useCallback(async () => {
    console.log('[Subscription] Refreshing subscription status...');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('[Subscription] No user found');
        setSubscriptionStatus(null);
        setIsLoading(false);
        return;
      }

      // ALWAYS check user_profiles as the source of truth for pro status
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('is_pro_member, subscription_expires_at')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        console.error('[Subscription] Error fetching profile:', profileError);
      }

      console.log('[Subscription] Profile data:', profile);

      // Determine pro status from user_profiles
      let userIsPro = false;
      let subscriptionDaysLeft = 0;
      
      if (profile?.is_pro_member && profile?.subscription_expires_at) {
        const expiresAt = new Date(profile.subscription_expires_at);
        userIsPro = expiresAt > new Date();
        subscriptionDaysLeft = userIsPro 
          ? Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) 
          : 0;
      }

      console.log('[Subscription] User is pro:', userIsPro, 'Days left:', subscriptionDaysLeft);

      // Get today's usage from daily_usage table
      const today = new Date().toISOString().split('T')[0];
      const { data: usageData } = await supabase
        .from('daily_usage')
        .select('ai_scans_count, manual_entries_count')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      const currentUsage = {
        ai_scans_count: usageData?.ai_scans_count || 0,
        manual_entries_count: usageData?.manual_entries_count || 0,
      };

      console.log('[Subscription] Daily usage:', currentUsage);

      setDailyUsage(currentUsage);

      // Calculate limits based on pro status
      const limit = userIsPro ? PRO_DAILY_AI_LIMIT : FREE_DAILY_AI_LIMIT;
      const remaining = Math.max(0, limit - currentUsage.ai_scans_count);

      // Check protocol generation eligibility
      let canGenProtocol = true;
      let daysUntilProtocol = 0;
      let protocolCount = 0;

      const { data: protocolData } = await supabase
        .from('user_protocols')
        .select('protocol_generated, generation_date')
        .eq('user_id', user.id)
        .maybeSingle();

      if (protocolData?.protocol_generated) {
        protocolCount = 1;
        if (!userIsPro) {
          // Free users can only generate once
          canGenProtocol = false;
        } else if (protocolData.generation_date) {
          // Pro users have cooldown
          const lastGen = new Date(protocolData.generation_date);
          const daysSinceGen = Math.floor((Date.now() - lastGen.getTime()) / (1000 * 60 * 60 * 24));
          const cooldownDays = 5;
          if (daysSinceGen < cooldownDays) {
            canGenProtocol = false;
            daysUntilProtocol = cooldownDays - daysSinceGen;
          }
        }
      }

      // Build the subscription status object
      const status: SubscriptionStatus = {
        user_id: user.id,
        is_pro: userIsPro,
        is_trial: !userIsPro,
        is_expired: false,
        trial_days_left: userIsPro ? 0 : 7,
        subscription_days_left: subscriptionDaysLeft,
        plan_name: userIsPro ? 'Pro' : 'Free',
        daily_ai_limit: limit,
        ai_scans_used: currentUsage.ai_scans_count,
        ai_scans_remaining: remaining,
        can_use_ai_scanner: remaining > 0,
        can_generate_protocol: canGenProtocol,
        days_until_protocol: daysUntilProtocol,
        protocol_generation_count: protocolCount,
      };

      console.log('[Subscription] Final status:', status);
      setSubscriptionStatus(status);

    } catch (error) {
      console.error('[Subscription] Error refreshing status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // AI SCAN CHECK & INCREMENT
  // =====================================================

  const checkAndIncrementAIScan = useCallback(async (): Promise<{ allowed: boolean; message: string; remaining?: number }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { allowed: false, message: 'Please sign in to use AI scanning' };
      }

      // Re-check current pro status from user_profiles
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_pro_member, subscription_expires_at')
        .eq('user_id', user.id)
        .single();

      let userIsPro = false;
      if (profile?.is_pro_member && profile?.subscription_expires_at) {
        userIsPro = new Date(profile.subscription_expires_at) > new Date();
      }

      const limit = userIsPro ? PRO_DAILY_AI_LIMIT : FREE_DAILY_AI_LIMIT;

      // Get today's usage
      const today = new Date().toISOString().split('T')[0];
      const { data: usageData } = await supabase
        .from('daily_usage')
        .select('ai_scans_count')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      const currentCount = usageData?.ai_scans_count || 0;

      console.log('[Subscription] AI Scan check - isPro:', userIsPro, 'limit:', limit, 'used:', currentCount);

      if (currentCount >= limit) {
        return {
          allowed: false,
          message: userIsPro 
            ? 'Daily AI scan limit reached (8). Try again tomorrow!'
            : 'Daily AI scan limit reached (4). Upgrade to Pro for 8 scans/day!',
          remaining: 0
        };
      }

      // Increment the count
      const { error: upsertError } = await supabase
        .from('daily_usage')
        .upsert({
          user_id: user.id,
          date: today,
          ai_scans_count: currentCount + 1,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,date'
        });

      if (upsertError) {
        console.error('[Subscription] Error updating usage:', upsertError);
        // Try insert if upsert fails
        await supabase
          .from('daily_usage')
          .insert({
            user_id: user.id,
            date: today,
            ai_scans_count: currentCount + 1,
          });
      }

      const newRemaining = limit - (currentCount + 1);

      // Update local state
      setDailyUsage(prev => ({
        ...prev,
        ai_scans_count: currentCount + 1
      }));

      if (subscriptionStatus) {
        setSubscriptionStatus({
          ...subscriptionStatus,
          ai_scans_used: currentCount + 1,
          ai_scans_remaining: newRemaining,
          can_use_ai_scanner: newRemaining > 0
        });
      }

      return {
        allowed: true,
        message: 'Scan allowed',
        remaining: newRemaining
      };

    } catch (error) {
      console.error('[Subscription] Error in checkAndIncrementAIScan:', error);
      return { allowed: false, message: 'Error checking scan limit. Please try again.' };
    }
  }, [subscriptionStatus]);

  // =====================================================
  // PROTOCOL GENERATION CHECK
  // =====================================================

  const checkProtocolGeneration = useCallback(async (): Promise<{ allowed: boolean; message: string; daysUntilNext?: number }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { allowed: false, message: 'Please sign in' };
      }

      // Check pro status
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_pro_member, subscription_expires_at')
        .eq('user_id', user.id)
        .single();

      let userIsPro = false;
      if (profile?.is_pro_member && profile?.subscription_expires_at) {
        userIsPro = new Date(profile.subscription_expires_at) > new Date();
      }

      // Check if user has generated a protocol
      const { data: protocolData } = await supabase
        .from('user_protocols')
        .select('protocol_generated, generation_date')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!protocolData?.protocol_generated) {
        // Never generated - allow
        return { allowed: true, message: 'You can generate a protocol' };
      }

      // Already generated
      if (!userIsPro) {
        // Free user - not allowed
        return {
          allowed: false,
          message: 'Free users can only generate one protocol. Upgrade to Pro for unlimited regenerations.'
        };
      }

      // Pro user - check cooldown
      if (protocolData.generation_date) {
        const lastGen = new Date(protocolData.generation_date);
        const daysSinceGen = Math.floor((Date.now() - lastGen.getTime()) / (1000 * 60 * 60 * 24));
        const cooldownDays = 5;
        
        if (daysSinceGen < cooldownDays) {
          const daysLeft = cooldownDays - daysSinceGen;
          return {
            allowed: false,
            message: `Protocol regeneration available in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`,
            daysUntilNext: daysLeft
          };
        }
      }

      return { allowed: true, message: 'You can regenerate your protocol' };

    } catch (error) {
      console.error('[Subscription] Error checking protocol generation:', error);
      return { allowed: true, message: 'Check failed, allowing generation' };
    }
  }, []);

  // =====================================================
  // RECORD PROTOCOL GENERATION
  // =====================================================

  const recordProtocolGeneration = useCallback(async (): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('user_protocols')
        .upsert({
          user_id: user.id,
          protocol_generated: true,
          generation_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      // Refresh status
      await refreshSubscriptionStatus();

    } catch (error) {
      console.error('[Subscription] Error recording protocol generation:', error);
    }
  }, [refreshSubscriptionStatus]);

  // =====================================================
  // LOAD PLANS
  // =====================================================

  const loadPlans = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('duration_months', { ascending: true });

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('[Subscription] Error loading plans:', error);
    }
  }, []);

  // =====================================================
  // CREATE CHECKOUT SESSION
  // =====================================================

  const createCheckoutSession = useCallback(async (planId: string): Promise<{ checkout_url: string } | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No active session');
        return null;
      }

      const response = await supabase.functions.invoke('create-checkout-session', {
        body: { plan_id: planId }
      });

      if (response.error) {
        console.error('Checkout session error:', response.error);
        return null;
      }

      return response.data;
    } catch (error) {
      console.error('[Subscription] Error creating checkout session:', error);
      return null;
    }
  }, []);

  // =====================================================
  // EFFECTS
  // =====================================================

  useEffect(() => {
    refreshSubscriptionStatus();
    loadPlans();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      console.log('[Subscription] Auth state changed:', event);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        refreshSubscriptionStatus();
      } else if (event === 'SIGNED_OUT') {
        setSubscriptionStatus(null);
        setDailyUsage({ ai_scans_count: 0, manual_entries_count: 0 });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [refreshSubscriptionStatus, loadPlans]);

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  const value: SubscriptionContextType = {
    subscriptionStatus,
    dailyUsage,
    plans,
    isLoading,
    isPro,
    isTrialActive,
    isExpired,
    canUseAIScanner,
    canGenerateProtocol,
    aiScansRemaining,
    daysUntilExpiry,
    refreshSubscriptionStatus,
    checkAndIncrementAIScan,
    checkProtocolGeneration,
    recordProtocolGeneration,
    createCheckoutSession,
    loadPlans,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// =====================================================
// HOOK
// =====================================================

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}